/**
 * Team Inbox — S2 Thread (UI Spec §7.2/§7.3).
 *
 * Date pills · unread divider · bubbles · full-screen media viewer · live
 * session banner · scroll-to-bottom FAB · text composer.
 *
 * Phase 2 additions: §7.3 allocation/escalation SYSTEM LINES merged into the
 * timeline, the "X is replying…" CLAIM banner (R-C2) and the composer-focus
 * claim heartbeat (refresh < the 90s TTL while typing; release on blur).
 *
 * Header-menu redesign (2026-06-22) — the stacked action bar + triage strip are
 * GONE; every action is collapsed behind a compact, tappable chat header
 * (<ThreadHeader/>): desktop = a right slide-over that merges the Context rows
 * with the Sort/Manage groups, mobile = a bottom sheet. Reply IS the composer
 * (no separate Reply button). The thread now fills the height. Applied to BOTH
 * channels (this WA Thread + EmailThread) so they stay consistent.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type AttachmentInput } from '../../../lib/messagesStore';
import type { OlderMessagesResult, ThreadData } from '../lib/inboxStore';
import { groupByDay, unreadDividerIndex } from '../lib/snippets';
import {
  CLAIM_REFRESH_MS,
  claimBannerFor,
  eventsToMessages,
  mergeTimeline,
} from '../lib/ownership';
import type {
  CannedReply,
  ClaimResult,
  InboxConversation,
  InboxMessage,
  NumberRegistry,
  Teammate,
  ThreadEvent,
} from '../lib/types';
import { Bubble, ProposalChip, isForwardableMessage } from '../components/bubbles';
import { Composer } from '../components/Composer';
import { MediaViewer } from '../components/MediaViewer';
import { SessionBanner, useSessionState } from '../components/SessionBanner';
import type { TriageAction } from './EmailThread';
import ThreadHeader from '../components/ThreadHeader';
import { receivedOnLabel } from '../lib/headerMenu';
import {
  findLeadForThread,
  leadChipModel,
} from '../../../lib/leadThreadJoin';
import type { Lead } from '../../../lib/leads';
import {
  ClaimBanner,
  DatePill,
  EmptyState,
  ScrollToBottomFab,
  SystemLine,
  UnreadDivider,
} from '../components/furniture';
import type { LiveClient } from '../../../lib/projectStore';

/**
 * Opt-in extra send channels for the P3 merged lens. WhatsApp always uses the
 * real <Composer/>; these add the internal-note + email send paths so nothing
 * the surfaces can do today is lost. A handler that is `undefined` renders a
 * muted explanation line instead of a control (no dead controls).
 */
export interface LensExtraChannels {
  /** Save an internal note (List B, postMessage). Undefined → note tab hidden. */
  onSendNote?: (text: string) => Promise<boolean>;
  /** Send an email to the client (List B, sendEmail). Undefined → email tab hidden. */
  onSendEmail?: (text: string) => Promise<boolean>;
  /** Muted line when notes are unavailable (e.g. contact has no linked project). */
  noteUnavailableReason?: string | null;
  /** Muted line when email is unavailable (no email/contact on file). */
  emailUnavailableReason?: string | null;
  /**
   * When set, WhatsApp is not available yet (no live conversation): the WA tab
   * shows this honest muted line instead of a composer. Notes/email still work.
   */
  waUnavailableReason?: string | null;
  /** Which channel the switcher opens on (defaults to WhatsApp). */
  defaultChannel?: 'wa' | 'note' | 'email';
}

/** Minimal note/email composer — reuses the `.ti-composer` shell (not a WA redraw). */
function LensExtraComposer({
  kind,
  onSend,
}: {
  kind: 'note' | 'email';
  onSend: (text: string) => Promise<boolean>;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function submit() {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const ok = await onSend(t);
      if (ok) setText('');
      else
        setErr(
          kind === 'email'
            ? 'The email did not go out. Try again.'
            : 'The note was not saved. Try again.',
        );
    } catch (e) {
      setErr((e as { message?: string })?.message || 'Send failed — try again.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className={`ti-lensbar ${kind}`}>
      <p className="ti-lensbar-hint">
        {kind === 'note'
          ? '🔒 Internal note — saved to the project, never sent to the client.'
          : '✉ Email — goes to the client’s email on file.'}
      </p>
      {err && (
        <div className="ti-send-err" role="alert">
          {err}
        </div>
      )}
      <div className="ti-composer">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void submit();
            }
          }}
          placeholder={
            kind === 'email' ? 'Write an email to the client…' : 'Write an internal note…'
          }
          aria-label={kind === 'email' ? 'Email body' : 'Internal note'}
          rows={1}
          disabled={busy}
        />
        <button
          type="button"
          className="ti-send"
          disabled={busy || !text.trim()}
          onClick={() => void submit()}
          aria-label={kind === 'email' ? 'Send email' : 'Save internal note'}
          title={kind === 'email' ? 'Send email' : 'Save internal note'}
        >
          {busy ? '…' : '➤'}
        </button>
      </div>
    </div>
  );
}

export default function Thread({
  conversation,
  thread,
  events,
  loading,
  sending,
  sendErr,
  unreadAtOpen,
  replyFrom,
  teammates,
  myId,
  isAdmin,
  canAssign,
  registry,
  linkProject = true,
  onBack,
  onSend,
  onSendTemplate,
  onSendCanned,
  canned = [],
  onReact,
  onForward,
  onForwardMany,
  forwardManyToken,
  forwardManyOpen = false,
  onAssign,
  onClaim,
  contacts = [],
  resolveContact,
  leads = [],
  onAttachError,
  onFetchOlderMessages,
  onConfirmProposal,
  onDismissProposal,
  onCreateTask,
  extraChannels,
  onCreateContact,
  onCreateLead,
  onAddToExisting,
  onTriage,
  triageBusy = false,
  initialPickerOpen,
}: {
  conversation: InboxConversation | null;
  thread: ThreadData | null;
  /** Phase 3b first-touch deep link — open the composer's send picker on mount. */
  initialPickerOpen?: boolean;
  /** §7.3 system lines (allocation/escalation events) for this thread. */
  events: ThreadEvent[];
  loading: boolean;
  sending: boolean;
  sendErr: string | null;
  /** The row's unread count CAPTURED at open (drives the divider). */
  unreadAtOpen: number;
  replyFrom: string | null;
  teammates: Teammate[];
  myId: string | null;
  isAdmin: boolean;
  canAssign: boolean;
  /** Number registry — resolves the receiving line → friendly "received on" chip. */
  registry: NumberRegistry;
  /** false in the dev harness (no router context) — disables the project link. */
  linkProject?: boolean;
  onBack: () => void;
  onSend: (text: string, attachment?: AttachmentInput) => Promise<boolean>;
  /** Send ONE approved template with ordered params (any time). */
  onSendTemplate?: (
    templateName: string,
    params: string[],
  ) => Promise<boolean>;
  /** Send ONE canned message (resolved plain text) — in-window only. */
  onSendCanned?: (text: string) => Promise<boolean>;
  /** Canned messages visible to this staff member (Send-Picker ⚡ tab). */
  canned?: CannedReply[];
  /** AD-028 — react to / un-react from ONE message (emoji '' removes). */
  onReact?: (targetWaMessageId: string, emoji: string) => Promise<boolean>;
  /** Forward ONE message's attachment to another chat (opens the target
   *  picker in the host). Wired only for forwardable media bubbles. */
  onForward?: (m: InboxMessage) => void;
  /**
   * Forward SEVERAL received files at once. Given the selected forwardable
   * messages (in thread order), the host opens the shared ForwardPicker and
   * loops `forwardAttachment` once per file. Undefined → the multi-select
   * entry point is hidden (no dead control).
   */
  onForwardMany?: (messages: InboxMessage[]) => void;
  /**
   * One-way "multi-forward completed" signal from the host. When this number
   * changes (host bumps it after ALL selected files forwarded OK), the thread
   * exits select mode and clears the selection. Left unchanged on cancel /
   * partial failure so the owner keeps their selection to retry.
   */
  forwardManyToken?: number;
  /**
   * True while the host's multi-forward picker (a modal) is open. The thread's
   * own Esc→exit-select handler stands down so Escape acts on the topmost
   * surface (the picker), never tearing down select mode underneath it.
   */
  forwardManyOpen?: boolean;
  onAssign: (toStaffId: string, note: string) => Promise<boolean>;
  /** Claim heartbeat — null when the data source has no claim support. */
  onClaim: ((focused: boolean) => Promise<ClaimResult | null>) | null;
  /** WA_TEAMINBOX_SEAM — already-loaded contacts for sender identity. */
  contacts?: LiveClient[];
  /** WA_TEAMINBOX_SEAM — resolver function (pure, no Firestore call). */
  resolveContact?: (phone: string, contacts: LiveClient[]) => LiveClient | null;
  /** AD-039 identity join — already-loaded leads; an EXACT phone match
   *  renders the lead chip in the thread header (computed, never created). */
  leads?: Lead[];
  onAttachError?: (msg: string | null) => void;
  /** Open the Create-Contact FORM for an unknown sender (host shows the modal). */
  onCreateContact?: (phone: string) => void;
  /** Open the Create-Lead FORM for an unknown sender (host shows the modal). */
  onCreateLead?: (phone: string) => void;
  /** Open the "Add to existing" picker (host shows the two-tab picker). */
  onAddToExisting?: (phone: string) => void;
  /**
   * Archive / Hold for an unknown WhatsApp sender (Create Lead / Create Contact
   * now go through the form callbacks above). The phone is carried so the
   * orchestrator resolves the canonical WA triage record + clears the flag.
   */
  onTriage?: (action: TriageAction, phone: string) => void;
  /** Triage write in flight — disables the menu's triage actions. */
  triageBusy?: boolean;
  /**
   * Fetches a page of messages older than `beforeMs` (the current
   * `nextCursor`). Called when the user scrolls to the top of the canvas.
   * Wired in TeamInboxView to `dataSource.fetchOlderMessages(key, beforeMs)`.
   */
  onFetchOlderMessages: (beforeMs: number) => Promise<OlderMessagesResult>;
  /** M3.3 — confirm an AI proposal (nodeType + projectIds + optional taskTitle). */
  onConfirmProposal?: (
    messageId: string,
    projectId: string,
    nodeType: string,
    projectIds: string[],
    taskTitle: string | null,
  ) => Promise<void>;
  /** M3.3 — dismiss an AI proposal chip. */
  onDismissProposal?: (messageId: string, projectId: string) => Promise<void>;
  /** M3.3 — create a Type-2 chat task from a confirmed message. */
  onCreateTask?: (
    messageId: string,
    projectId: string,
    contactId: string,
    title: string,
  ) => Promise<void>;
  /**
   * P3 lens merge (opt-in) — when set, a channel switcher sits above the
   * composer so this ONE thread can also send INTERNAL NOTES and EMAIL
   * alongside WhatsApp (WhatsApp still goes through the real <Composer/>).
   * The main Team Inbox passes nothing here → its composer is unchanged.
   */
  extraChannels?: LensExtraChannels;
}) {
  const [viewer, setViewer] = useState<InboxMessage | null>(null);
  const [showFab, setShowFab] = useState(false);
  const [claimConflict, setClaimConflict] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  // Header-menu open state (slide-over on desktop / bottom sheet on mobile).
  const [menuOpen, setMenuOpen] = useState(false);
  // P3 lens merge — which channel the (opt-in) switcher is composing on.
  const [lensCh, setLensCh] = useState<'wa' | 'note' | 'email'>(
    extraChannels?.defaultChannel ?? 'wa',
  );
  // Multi-select forward — "pick several received files, then forward them all
  // to one chat/project". `selectMode` gates the per-bubble checkboxes; the
  // Set holds the selected message ids.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const exitSelect = useCallback(() => {
    setSelectMode(false);
    setSelectedIds(new Set());
  }, []);
  const toggleSelect = useCallback((m: InboxMessage) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(m.id)) next.delete(m.id);
      else next.add(m.id);
      return next;
    });
  }, []);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const composerWrapRef = useRef<HTMLDivElement | null>(null);
  const focusedRef = useRef(false);
  const session = useSessionState(thread ? thread.lastInboundMs : null);

  // ── pagination state (Module D) ──────────────────────────────────
  // `extMessages` is the displayed message list — starts from the initial
  // thread load, grows downward on live polls, and grows upward when the
  // user scrolls to the top ("load older").
  const [extMessages, setExtMessages] = useState<InboxMessage[]>(
    thread?.messages ?? [],
  );
  const [hasMore, setHasMore] = useState(thread?.hasMore ?? false);
  const [nextCursor, setNextCursor] = useState<number | null>(
    thread?.nextCursor ?? null,
  );
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  // Tracks which conversation key the current extMessages belong to so
  // poll updates are merged rather than replacing the full list.
  const prevKeyRef = useRef<string | null>(null);

  // Sync thread → extMessages. Two paths:
  //   key change   → fresh chat open: replace everything, reset pagination.
  //   same key     → live poll: merge NEW messages at the bottom by id.
  useEffect(() => {
    if (!thread) {
      setExtMessages([]);
      setHasMore(false);
      setNextCursor(null);
      prevKeyRef.current = null;
      return;
    }
    if (prevKeyRef.current !== thread.key) {
      setExtMessages(thread.messages);
      setHasMore(thread.hasMore ?? false);
      setNextCursor(thread.nextCursor ?? null);
      prevKeyRef.current = thread.key;
    } else {
      // Live poll: append any messages not already in extMessages (by id).
      setExtMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        const newMsgs = thread.messages.filter((m) => !seen.has(m.id));
        if (newMsgs.length === 0) return prev;
        return [...prev, ...newMsgs];
      });
    }
  }, [thread]);

  const scrollToBottom = useCallback(() => {
    const el = canvasRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  // Keep scroll at the bottom when dynamically loaded images/media finish loading
  const handleMediaLoad = useCallback(() => {
    const el = canvasRef.current;
    if (el) {
      const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 300;
      if (isNearBottom) {
        el.scrollTop = el.scrollHeight;
      }
    }
  }, []);

  // Scroll to bottom only when the conversation KEY changes (new open) and messages are loaded.
  // Poll updates merge at the bottom without disturbing the user's position
  // — the scroll-to-bottom FAB signals new messages when scrolled up.
  const lastScrolledKeyRef = useRef<string | null>(null);
  const threadKey = thread?.key ?? null;
  useEffect(() => {
    if (!threadKey) {
      lastScrolledKeyRef.current = null;
      return;
    }
    // Determine if the messages for the current threadKey have loaded
    const hasMessagesLoaded = !loading && (thread ? extMessages.length >= thread.messages.length : true);

    if (hasMessagesLoaded && lastScrolledKeyRef.current !== threadKey) {
      const rafId = requestAnimationFrame(() => {
        scrollToBottom();
      });
      // Double check scroll-to-bottom after a short delay (for late audio player / layout renders)
      const timerId = setTimeout(() => {
        scrollToBottom();
      }, 350);
      lastScrolledKeyRef.current = threadKey;
      return () => {
        cancelAnimationFrame(rafId);
        clearTimeout(timerId);
      };
    }
  }, [threadKey, loading, extMessages, thread, scrollToBottom]);

  // "Load older messages" — triggered by scrolling near the top of the canvas.
  const loadOlderMessages = useCallback(async () => {
    if (!hasMore || isLoadingOlder || nextCursor == null) return;
    const el = canvasRef.current;
    const prevScrollHeight = el?.scrollHeight ?? 0;
    setIsLoadingOlder(true);
    try {
      const result = await onFetchOlderMessages(nextCursor);
      setExtMessages((prev) => [...result.messages, ...prev]);
      setHasMore(result.hasMore);
      setNextCursor(result.nextCursor);
      // Restore scroll position so the view does not jump after prepend.
      requestAnimationFrame(() => {
        if (el) el.scrollTop = el.scrollHeight - prevScrollHeight;
      });
    } catch {
      /* best-effort — a failed page leaves the current list intact */
    } finally {
      setIsLoadingOlder(false);
    }
  }, [hasMore, isLoadingOlder, nextCursor, onFetchOlderMessages]);

  const LOAD_OLDER_THRESHOLD_PX = 80;
  const onScroll = useCallback(() => {
    const el = canvasRef.current;
    if (!el) return;
    setShowFab(el.scrollHeight - el.scrollTop - el.clientHeight > 240);
    if (el.scrollTop < LOAD_OLDER_THRESHOLD_PX) {
      void loadOlderMessages();
    }
  }, [loadOlderMessages]);

  // ── claim heartbeat (R-C2) ──────────────────────────────────────
  // Composer focused → claim NOW + refresh every CLAIM_REFRESH_MS (< TTL).
  // Blur / thread switch / unmount → release. A conflict (someone else
  // holds the live lock) surfaces as the claim banner immediately.
  const conversationKey = conversation?.key || null;
  const claimTick = useCallback(
    async (focused: boolean) => {
      if (!onClaim) return;
      try {
        const r = await onClaim(focused);
        if (r && r.conflict && r.claimedByName) {
          setClaimConflict(`${r.claimedByName} is replying…`);
        } else {
          setClaimConflict(null);
        }
      } catch {
        /* heartbeat is best-effort */
      }
    },
    [onClaim],
  );

  const handleFocusChange = useCallback(
    (focused: boolean) => {
      focusedRef.current = focused;
      void claimTick(focused);
    },
    [claimTick],
  );

  useEffect(() => {
    if (!conversationKey || !onClaim) return;
    const id = window.setInterval(() => {
      if (focusedRef.current) void claimTick(true);
    }, CLAIM_REFRESH_MS);
    return () => {
      window.clearInterval(id);
      // Leaving the thread releases our claim (if any).
      if (focusedRef.current) {
        focusedRef.current = false;
        void claimTick(false);
      }
      setClaimConflict(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationKey]);

  // Close the actions menu when the conversation changes.
  useEffect(() => {
    setMenuOpen(false);
    // Switching chats abandons any in-progress multi-select (its ids belong to
    // the previous thread's messages).
    exitSelect();
    // Re-open the lens switcher on its default channel for the new conversation
    // (e.g. a contact with no live WhatsApp defaults to the Internal-note tab).
    if (extraChannels?.defaultChannel) setLensCh(extraChannels.defaultChannel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationKey]);

  // Host signalled a fully-successful multi-forward → leave select mode and
  // clear the selection. Ignored on first mount (no token change yet).
  const prevForwardTokenRef = useRef<number | undefined>(forwardManyToken);
  useEffect(() => {
    if (
      forwardManyToken !== undefined &&
      prevForwardTokenRef.current !== undefined &&
      forwardManyToken !== prevForwardTokenRef.current
    ) {
      exitSelect();
    }
    prevForwardTokenRef.current = forwardManyToken;
  }, [forwardManyToken, exitSelect]);

  // Esc leaves select mode — but only when nothing that should intercept Esc
  // first is open: not the actions sheet, and NOT the multi-forward picker
  // (that modal owns Escape while it is up, so select mode is not torn down
  // underneath it).
  useEffect(() => {
    if (!selectMode || menuOpen || forwardManyOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') exitSelect();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectMode, menuOpen, forwardManyOpen, exitSelect]);

  // ── timeline: messages + §7.3 system lines, day-grouped ─────────
  // Uses extMessages (the paginated, poll-merged list) rather than
  // thread.messages so the full scrollback history renders correctly.
  // M3.3: archived messages are hidden unless the user reveals them.
  const hasArchivedMessages = useMemo(
    () => extMessages.some((m) => m.nodeType === 'archive'),
    [extMessages],
  );
  const visibleMessages = useMemo(
    () => showArchived ? extMessages : extMessages.filter((m) => m.nodeType !== 'archive'),
    [extMessages, showArchived],
  );
  const timeline = useMemo(
    () =>
      thread
        ? mergeTimeline(visibleMessages, eventsToMessages(events))
        : [],
    [thread, visibleMessages, events],
  );
  const groups = useMemo(
    () => (thread ? groupByDay(timeline, Date.now()) : []),
    [thread, timeline],
  );
  const dividerIdx = useMemo(
    () => (thread ? unreadDividerIndex(timeline, unreadAtOpen) : -1),
    [thread, timeline, unreadAtOpen],
  );

  // AD-028 — optimistic react: pin (or remove) the 'self' badge immediately,
  // then call the backend; revert the local list if the send fails.
  // NOTE: this hook MUST stay above the `if (!conversation)` early return —
  // a hook after a conditional return changes the hook count between renders
  // ("Rendered more hooks than during the previous render") and white-screens
  // the thread when a conversation is opened.
  const handleReact = useCallback(
    async (targetWaMessageId: string, emoji: string) => {
      if (!onReact) return;
      const patch = (list: InboxMessage[]) =>
        list.map((m) => {
          if ((m.waMessageId || m.id) !== targetWaMessageId) return m;
          const reactions = { ...(m.reactions || {}) };
          if (emoji) reactions['self'] = { emoji, at: Date.now(), by: 'self' };
          else delete reactions['self'];
          return { ...m, reactions };
        });
      const before = extMessages;
      setExtMessages(patch);
      try {
        const ok = await onReact(targetWaMessageId, emoji);
        if (!ok) setExtMessages(before); // revert on a soft failure
      } catch {
        setExtMessages(before); // revert on error
      }
    },
    [onReact, extMessages],
  );

  // The currently-selected messages that are STILL forwardable, in thread
  // order — the exact list handed to the host's multi-forward loop. Filtering
  // by isForwardableMessage here means a poll that drops a message can never
  // send a stale/unforwardable id (text OR media).
  const selectedForwardable = useMemo(
    () => extMessages.filter((m) => selectedIds.has(m.id) && isForwardableMessage(m)),
    [extMessages, selectedIds],
  );
  const selectedCount = selectedForwardable.length;

  if (!conversation) {
    return (
      <div className="ti-thread">
        <div className="ti-thread-empty">
          <EmptyState icon="💬">
            Pick a conversation on the left to read and reply.
          </EmptyState>
        </div>
      </div>
    );
  }

  // The poll-merged claim (meta) OR the immediate conflict from our own
  // heartbeat — whichever knows about another live typist.
  const claimText =
    claimBannerFor(conversation, myId, Date.now()) || claimConflict;

  // The resolved known contact (sender identity) + the receiving-line label.
  const resolved =
    resolveContact && conversation.phone
      ? resolveContact(conversation.phone, contacts)
      : null;
  const received = receivedOnLabel(conversation, registry);
  const phone = conversation.phone || '';

  // AD-039 identity join — the ONE lead this conversation's phone joins to
  // (EXACT match only; computed live, nothing is created or written). Agent
  // (AI-bot) threads never join a lead.
  const joinedLead =
    phone && !conversation.aiBot
      ? findLeadForThread(phone, null, leads)
      : null;
  const leadChip = joinedLead ? leadChipModel(joinedLead) : null;

  // Map a message's position in the flat ascending list (for the divider).
  let flatIndex = -1;

  return (
    <div className="ti-thread">
      {/* compact clickable header + the actions slide-over / bottom sheet.
          Reply IS the composer below (no separate Reply button). */}
      <ThreadHeader
        conversation={conversation}
        received={received}
        resolved={resolved}
        lead={leadChip}
        myId={myId}
        isAdmin={isAdmin}
        canAssign={canAssign}
        teammates={teammates}
        linkProject={linkProject}
        open={menuOpen}
        onOpen={() => setMenuOpen(true)}
        onClose={() => setMenuOpen(false)}
        onBack={onBack}
        busy={triageBusy}
        actions={{
          onCreateLead: phone ? () => onCreateLead?.(phone) : undefined,
          onCreateContact: phone ? () => onCreateContact?.(phone) : undefined,
          onAddToExisting:
            onAddToExisting && phone ? () => onAddToExisting(phone) : undefined,
          onArchive: phone ? () => onTriage?.('archive', phone) : undefined,
          onHold: phone ? () => onTriage?.('hold', phone) : undefined,
          onAssign: (toStaffId) => void onAssign(toStaffId, ''),
          // Only offer the multi-select entry point when the host can run the
          // forward loop (no dead menu item otherwise).
          onSelectForward: onForwardMany
            ? () => {
                setMenuOpen(false);
                setSelectedIds(new Set());
                setSelectMode(true);
              }
            : undefined,
        }}
      />

      <div
        className="ti-canvas"
        ref={canvasRef}
        onScroll={onScroll}
        data-testid="ti-canvas"
      >
        {/* ── Load-older chrome (Module D) ─────────────────────── */}
        {isLoadingOlder && (
          <div className="ti-load-older">
            <span className="ti-load-older-label">Loading…</span>
          </div>
        )}
        {!hasMore && extMessages.length > 0 && !loading && (
          <div className="ti-beginning-pill">Beginning of conversation</div>
        )}
        {/* M3.3: show-archived toggle */}
        {hasArchivedMessages && !loading && (
          <div className="ti-archived-toggle">
            <button
              type="button"
              className="ti-archived-toggle-btn"
              onClick={() => setShowArchived((v) => !v)}
            >
              {showArchived ? '🗄 Hide archived messages' : `🗄 Show archived messages`}
            </button>
          </div>
        )}
        {loading && (
          <EmptyState icon="◌">Loading messages…</EmptyState>
        )}
        {!loading && thread && timeline.length === 0 && (
          <EmptyState icon="🪷">No messages yet.</EmptyState>
        )}
        {!loading &&
          groups.map((g) => (
            <div key={g.dayStartMs}>
              <DatePill label={g.label} />
              {g.messages.map((m) => {
                flatIndex += 1;
                const divider =
                  dividerIdx >= 0 && flatIndex === dividerIdx ? (
                    <UnreadDivider count={unreadAtOpen} />
                  ) : null;
                const projId = conversation?.projectId || '';
                const contactId = m.contactId || '';
                return (
                  <div key={m.id}>
                    {divider}
                    {m.kind === 'system' ? (
                      <SystemLine text={m.body} />
                    ) : (
                      <>
                        <Bubble
                          m={m}
                          onOpenMedia={setViewer}
                          onMediaLoad={handleMediaLoad}
                          onReact={onReact ? handleReact : undefined}
                          onForward={onForward}
                          selectMode={selectMode}
                          selected={selectedIds.has(m.id)}
                          onToggleSelect={toggleSelect}
                        />
                        {m.direction === 'in' && m.aiProposal && !m.aiProposal.confirmedBy && (
                          <ProposalChip
                            m={m}
                            onConfirm={async (nt, pids, title) => {
                              await onConfirmProposal?.(m.id, projId, nt, pids, title);
                            }}
                            onDismiss={async () => {
                              await onDismissProposal?.(m.id, projId);
                            }}
                            onCreateTask={async (title) => {
                              await onCreateTask?.(m.id, projId, contactId, title);
                            }}
                          />
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        {showFab && (
          <ScrollToBottomFab pending={0} onClick={scrollToBottom} />
        )}
      </div>

      {/* ── Multi-select forward bar ──
          Shown while select mode is on. Cancel + the live count are always
          present (both do something — no dead control); the "Forward N
          messages" button appears only once ≥1 forwardable message (text or
          file) is selected. */}
      {selectMode && (
        <div
          className="ti-selectbar"
          role="region"
          aria-label="Forward selected messages"
        >
          <span className="ti-selectbar-count" aria-live="polite">
            {selectedCount === 0
              ? 'Tap a message to select it, then Forward.'
              : `${selectedCount} message${selectedCount === 1 ? '' : 's'} selected`}
          </span>
          <span className="ti-selectbar-actions">
            <button
              type="button"
              className="ti-selectbar-cancel"
              onClick={exitSelect}
            >
              Cancel
            </button>
            {selectedCount > 0 && (
              <button
                type="button"
                className="ti-selectbar-go"
                onClick={() => onForwardMany?.(selectedForwardable)}
              >
                <span aria-hidden>↪</span> Forward {selectedCount} message
                {selectedCount === 1 ? '' : 's'}
              </button>
            )}
          </span>
        </div>
      )}

      {/* ── Compose region ──
          WhatsApp always uses the real <Composer/> (24h window, ⚡ 📋 📎 😀).
          When `extraChannels` is set (the P3 merged lens) a channel switcher
          adds the internal-note + email send paths above it. */}
      {thread &&
        (extraChannels ? (
          <div className="ti-lenscompose">
            <div className="ti-chswitch" role="group" aria-label="Reply channel">
              <button
                type="button"
                aria-pressed={lensCh === 'wa'}
                onClick={() => setLensCh('wa')}
              >
                💬 WhatsApp
              </button>
              {extraChannels.onSendNote && (
                <button
                  type="button"
                  aria-pressed={lensCh === 'note'}
                  onClick={() => setLensCh('note')}
                >
                  🔒 Internal note
                </button>
              )}
              {extraChannels.onSendEmail && (
                <button
                  type="button"
                  aria-pressed={lensCh === 'email'}
                  onClick={() => setLensCh('email')}
                >
                  ✉ Email
                </button>
              )}
            </div>

            {lensCh === 'wa' &&
              (extraChannels.waUnavailableReason ? (
                <p className="ti-lens-muted">{extraChannels.waUnavailableReason}</p>
              ) : (
                <>
                  {claimText && <ClaimBanner text={claimText} />}
                  <SessionBanner state={session} />
                  {sendErr && (
                    <div className="ti-send-err" role="alert">
                      {sendErr}
                    </div>
                  )}
                  <div ref={composerWrapRef}>
                    <Composer
                      session={session}
                      replyFrom={replyFrom}
                      sending={sending}
                      onSend={onSend}
                      onSendTemplate={onSendTemplate}
                      onSendCanned={onSendCanned}
                      approvedTemplates={thread.approvedTemplates || []}
                      canned={canned}
                      nameCtx={{
                        contactFirst: resolved?.name || null,
                        waProfileName: conversation.waProfileName || null,
                      }}
                      onFocusChange={onClaim ? handleFocusChange : undefined}
                      conversation={conversation}
                      onAttachError={onAttachError}
                      initialPickerOpen={initialPickerOpen}
                    />
                  </div>
                </>
              ))}

            {lensCh === 'note' && extraChannels.onSendNote && (
              <LensExtraComposer kind="note" onSend={extraChannels.onSendNote} />
            )}
            {lensCh === 'email' && extraChannels.onSendEmail && (
              <LensExtraComposer kind="email" onSend={extraChannels.onSendEmail} />
            )}

            {/* Not-yet-available paths described in muted text — never a dead control. */}
            {!extraChannels.onSendNote && extraChannels.noteUnavailableReason && (
              <p className="ti-lens-muted">{extraChannels.noteUnavailableReason}</p>
            )}
            {!extraChannels.onSendEmail && extraChannels.emailUnavailableReason && (
              <p className="ti-lens-muted">{extraChannels.emailUnavailableReason}</p>
            )}
          </div>
        ) : (
          <>
            {claimText && <ClaimBanner text={claimText} />}
            <SessionBanner state={session} />
            {sendErr && (
              <div className="ti-send-err" role="alert">
                {sendErr}
              </div>
            )}
            <div ref={composerWrapRef}>
              <Composer
                session={session}
                replyFrom={replyFrom}
                sending={sending}
                onSend={onSend}
                onSendTemplate={onSendTemplate}
                onSendCanned={onSendCanned}
                approvedTemplates={thread.approvedTemplates || []}
                canned={canned}
                nameCtx={{
                  contactFirst: resolved?.name || null,
                  waProfileName: conversation.waProfileName || null,
                }}
                onFocusChange={onClaim ? handleFocusChange : undefined}
                conversation={conversation}
                onAttachError={onAttachError}
                initialPickerOpen={initialPickerOpen}
              />
            </div>
          </>
        ))}

      {viewer && <MediaViewer m={viewer} onClose={() => setViewer(null)} />}
    </div>
  );
}
